import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Store, Plus, Search, Trash2, Pencil, TrendingUp, TrendingDown, MinusCircle, Loader2, IndianRupee, Clock, CheckCircle2, Download, Upload, FileSpreadsheet,
} from "lucide-react";

const CATEGORY_OPTIONS = ["Machinery", "Spares & Jigs", "Consumables", "Services", "Other"];

const MANAGE_ROLES = ["admin", "admin_addl", "manager", "accounts_manager", "stores_manager"];

export default function Vendors() {
  const { user } = useAuth();
  const canManage = MANAGE_ROLES.includes(user?.role);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // vendor object or {} for new
  const [showInactive, setShowInactive] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vendors", { params: { include_inactive: showInactive } });
      setVendors(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load vendors");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [showInactive]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return vendors;
    return vendors.filter(v =>
      (v.name || "").toLowerCase().includes(n) ||
      (v.gstin || "").toLowerCase().includes(n) ||
      (v.categories || []).some(c => (c || "").toLowerCase().includes(n))
    );
  }, [vendors, q]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("assetiq_token");
      const res = await fetch(`${API_BASE}/vendors/export.xlsx`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Vendors_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Export failed");
    } finally { setExporting(false); }
  };

  const del = async (v) => {
    if (!window.confirm(`Mark vendor "${v.name}" as inactive? Historical POs remain.`)) return;
    try {
      await api.delete(`/vendors/${v.id}`);
      toast.success("Vendor deactivated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not deactivate");
    }
  };

  const summary = useMemo(() => {
    const totalSpend = filtered.reduce((s, v) => s + (v.metrics?.total_spend || 0), 0);
    const totalPOs = filtered.reduce((s, v) => s + (v.metrics?.po_count || 0), 0);
    return { totalSpend, totalPOs };
  }, [filtered]);

  return (
    <div className="p-6 md:p-8 space-y-5" data-testid="vendors-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#fcf0f3] text-[#7C1D3A] text-[10px] font-semibold tracking-widest uppercase">
            <Store className="w-3 h-3" /> Procurement
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-2">Vendor Master</h1>
          <p className="text-sm text-slate-600 mt-1">
            Single source of truth for suppliers with live performance scoring from your POs.
          </p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={exportExcel} disabled={exporting} data-testid="export-vendors-btn">
              <Download className="w-4 h-4 mr-2" /> {exporting ? "Exporting..." : "Export Excel"}
            </Button>
            <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-import-vendors-btn">
              <Upload className="w-4 h-4 mr-2" /> Bulk Import
            </Button>
            <Button onClick={() => setEditing({})} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="add-vendor-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Vendor
            </Button>
          </div>
        )}
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Active Vendors" value={vendors.filter(v => v.active).length} icon={Store} />
        <StatCard label="Total POs" value={summary.totalPOs} icon={CheckCircle2} />
        <StatCard label="Total Spend" value={`₹ ${summary.totalSpend.toLocaleString('en-IN')}`} icon={IndianRupee} />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by name, GSTIN, or category…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid="vendor-search"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            data-testid="show-inactive-toggle"
          />
          Show inactive
        </label>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-8 flex items-center justify-center text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-sm">
              No vendors match. {canManage && "Click 'Add Vendor' to create one."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Vendor</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-600">GSTIN</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Category</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-slate-600">POs</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Spend</th>
                  <th className="text-center px-4 py-2.5 font-semibold text-slate-600">On-Time %</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-slate-600">Avg Delay</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-600">Last PO</th>
                  {canManage && <th className="px-4 py-2.5 w-24"></th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((v) => (
                  <tr key={v.id} className={`border-b border-slate-100 hover:bg-slate-50 ${!v.active ? "opacity-50" : ""}`} data-testid={`vendor-row-${v.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 flex items-center gap-1.5">
                        {v.name}
                        {!v.active && <span className="text-[9px] font-bold uppercase text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">inactive</span>}
                      </div>
                      {v.email && <div className="text-[11px] text-slate-500 mt-0.5">{v.email}</div>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{v.gstin || "—"}</td>
                    <td className="px-4 py-3 text-xs">
                      {(v.categories || []).length === 0 ? (
                        <span className="text-slate-300">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(v.categories || []).map((c) => (
                            <span key={c} className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-medium">
                              {c}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{v.metrics?.po_count ?? 0}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {v.metrics?.total_spend ? `₹ ${v.metrics.total_spend.toLocaleString('en-IN')}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <OnTimeBadge pct={v.metrics?.on_time_pct} />
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-xs">
                      {v.metrics?.avg_delay_days == null ? "—" :
                        v.metrics.avg_delay_days > 0 ? `+${v.metrics.avg_delay_days}d` :
                        v.metrics.avg_delay_days === 0 ? "on time" : `${v.metrics.avg_delay_days}d`}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{v.metrics?.last_po_date || "—"}</td>
                    {canManage && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setEditing(v)}
                            className="text-slate-500 hover:text-[#7C1D3A] p-1.5 rounded hover:bg-[#fcf0f3]"
                            data-testid={`edit-vendor-${v.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          {v.active && (
                            <button
                              onClick={() => del(v)}
                              className="text-slate-500 hover:text-red-700 p-1.5 rounded hover:bg-red-50"
                              data-testid={`delete-vendor-${v.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {editing !== null && (
        <VendorForm
          vendor={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {bulkOpen && (
        <BulkImportVendorsDialog
          open={bulkOpen}
          onOpenChange={setBulkOpen}
          onDone={() => { setBulkOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function BulkImportVendorsDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async (format) => {
    const token = localStorage.getItem("assetiq_token");
    try {
      const res = await fetch(`${API_BASE}/vendors/bulk/template?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `vendors_template.${format}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Template download failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/vendors/bulk", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Processed ${data.total_rows} rows · ${data.created} new · ${data.updated} updated`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="bulk-vendors-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#7C1D3A]" /> Bulk Import Vendors
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <form onSubmit={submit} className="space-y-4">
            <div className="p-4 bg-[#fcf0f3] border border-[#f5c4d3] rounded-lg text-sm">
              <div className="font-semibold text-[#7C1D3A] mb-1">1. Download the template</div>
              <p className="text-xs text-slate-600 mb-2">
                Required: <b>name</b>. Optional: gstin, categories (comma-separated e.g. <span className="font-mono">&quot;Machinery, Spares &amp; Jigs&quot;</span>), email, phone, address, credit_terms, notes.
                <br />If a vendor with the same name already exists, its data is updated (categories are merged).
              </p>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => downloadTemplate("xlsx")} data-testid="download-vendor-xlsx-template">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Excel Template
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => downloadTemplate("csv")} data-testid="download-vendor-csv-template">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> CSV Template
                </Button>
              </div>
            </div>

            <div className="p-4 border border-dashed border-slate-300 rounded-lg text-sm">
              <div className="font-semibold text-slate-700 mb-2">2. Upload your filled file</div>
              <Input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="bulk-vendors-file-input"
                className="cursor-pointer"
              />
              {file && (
                <div className="mt-2 text-xs text-slate-600 flex items-center gap-2">
                  <FileSpreadsheet className="w-3.5 h-3.5" /> {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="submit" disabled={!file || uploading} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="bulk-vendors-upload-submit">
                {uploading ? "Uploading..." : "Import Vendors"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-3" data-testid="bulk-vendors-result">
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded">
                <div className="text-2xl font-bold text-emerald-700">{result.created}</div>
                <div className="text-xs text-emerald-700">New vendors</div>
              </div>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <div className="text-2xl font-bold text-blue-700">{result.updated}</div>
                <div className="text-xs text-blue-700">Updated existing</div>
              </div>
              <div className="p-3 bg-red-50 border border-red-200 rounded">
                <div className="text-2xl font-bold text-red-700">{result.failed}</div>
                <div className="text-xs text-red-700">Errors</div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="max-h-48 overflow-y-auto border rounded p-2 text-xs">
                {result.errors.map((e, i) => (
                  <div key={`err-${e.row}-${i}`} className="text-red-700 py-0.5">
                    Row {e.row} ({e.name || "?"}): {e.error}
                  </div>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button onClick={onDone} className="bg-[#7C1D3A] hover:bg-[#6c1c36]">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="p-4 rounded-lg border border-slate-200 bg-white">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
          <div className="mt-1 font-heading text-2xl font-bold text-slate-900">{value}</div>
        </div>
        <div className="w-9 h-9 rounded-md bg-[#fcf0f3] text-[#7C1D3A] flex items-center justify-center">
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}

function OnTimeBadge({ pct }) {
  if (pct == null) return <span className="text-slate-300 text-xs">—</span>;
  let color = "bg-slate-100 text-slate-600";
  let Icon = MinusCircle;
  if (pct >= 90) { color = "bg-emerald-100 text-emerald-700"; Icon = TrendingUp; }
  else if (pct >= 60) { color = "bg-amber-100 text-amber-700"; Icon = MinusCircle; }
  else { color = "bg-red-100 text-red-700"; Icon = TrendingDown; }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${color}`}>
      <Icon className="w-3 h-3" /> {pct}%
    </span>
  );
}

function VendorForm({ vendor, onClose, onSaved }) {
  const isNew = !vendor?.id;
  const [form, setForm] = useState({
    name: vendor?.name || "",
    gstin: vendor?.gstin || "",
    address: vendor?.address || "",
    email: vendor?.email || "",
    phone: vendor?.phone || "",
    credit_terms: vendor?.credit_terms || "Net 30",
    categories: vendor?.categories || (vendor?.category ? [vendor.category] : []),
    notes: vendor?.notes || "",
    active: vendor?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleCat = (c) => setForm(f => ({
    ...f,
    categories: f.categories.includes(c) ? f.categories.filter(x => x !== c) : [...f.categories, c],
  }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Vendor name is required");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/vendors", form);
        toast.success("Vendor created");
      } else {
        await api.patch(`/vendors/${vendor.id}`, form);
        toast.success("Vendor updated");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="vendor-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Store className="w-5 h-5 text-[#7C1D3A]" /> {isNew ? "Add Vendor" : "Edit Vendor"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Vendor Name *</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="vendor-name-input" />
            </div>
            <div>
              <Label>GSTIN</Label>
              <Input value={form.gstin} onChange={(e) => set("gstin", e.target.value.toUpperCase())} maxLength={15} placeholder="15 chars" className="font-mono" />
            </div>
            <div>
              <Label>Category(ies)</Label>
              <div className="mt-1 flex flex-wrap gap-2 p-2 rounded-md border border-slate-200 bg-slate-50" data-testid="vendor-categories">
                {CATEGORY_OPTIONS.map((c) => (
                  <label
                    key={c}
                    className={`text-xs px-2.5 py-1 rounded-full cursor-pointer border transition-colors ${
                      form.categories.includes(c)
                        ? "bg-[#7C1D3A] text-white border-[#7C1D3A]"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={form.categories.includes(c)}
                      onChange={() => toggleCat(c)}
                      className="sr-only"
                      data-testid={`vendor-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    />
                    {c}
                  </label>
                ))}
              </div>
              <p className="text-[10px] text-slate-500 mt-1">One vendor may supply multiple categories.</p>
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 ..." />
            </div>
            <div>
              <Label>Credit Terms</Label>
              <Input value={form.credit_terms} onChange={(e) => set("credit_terms", e.target.value)} placeholder="Net 30 / Advance" />
            </div>
            <div>
              <Label>Status</Label>
              <select value={form.active ? "yes" : "no"} onChange={(e) => set("active", e.target.value === "yes")} className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm mt-1" disabled={isNew}>
                <option value="yes">Active</option>
                <option value="no">Inactive</option>
              </select>
            </div>
            <div className="col-span-2">
              <Label>Address</Label>
              <Textarea rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Internal notes for procurement team…" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="save-vendor-btn">
              {saving ? "Saving..." : (isNew ? "Create Vendor" : "Save Changes")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
